import { useState } from "react";

import type { VisibilityState } from "@tanstack/react-table";

/** Structurally compatible with `ColumnDef`, whose `id` is optional. */
export interface HidableColumn {
  id?: string;
  enableHiding?: boolean;
}

/**
 * Owns the persisted column visibility state for a table.
 *
 * Call this in the component that renders the columns filter. Pass the result
 * into `useDataTable` to keep the table controlled, so the filter can live
 * outside of (and above) the component holding the table instance.
 */
export default function useColumnVisibility(
  localStorageId: string,
  columns:
    | readonly HidableColumn[]
    | Record<string, { enableHiding?: boolean; [key: string]: unknown }>,
) {
  const visibilityStateKey = `table-${localStorageId}/columns`;

  const [columnVisibility, setState] = useState<VisibilityState>(() => {
    const localStorageData = localStorage.getItem(visibilityStateKey);
    if (localStorageData) {
      return JSON.parse(localStorageData);
    }
    const entries: [string, { enableHiding?: boolean }][] = Array.isArray(
      columns,
    )
      ? columns.map(({ id, ...column }) => [id, column])
      : Object.entries(columns);

    return entries.reduce((accumulator, [id, { enableHiding }]) => {
      // columns without an explicit id are left out; react-table treats a
      // missing entry as visible
      if (id && enableHiding !== false) {
        accumulator[id] = true;
      }
      return accumulator;
    }, {} as VisibilityState);
  });

  function setColumnVisibility(newVisibilityState: VisibilityState) {
    setState(newVisibilityState);
    localStorage.setItem(
      visibilityStateKey,
      JSON.stringify(newVisibilityState),
    );
  }

  return { columnVisibility, setColumnVisibility };
}
